import { useEffect, useReducer, useRef } from "react";
import { doc, onSnapshot, type DocumentSnapshot, type FirestoreError } from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import {
  toAthleteRadarProfile,
  type AthleteRadarProfile,
  type RadarAxis,
} from "@lemonadetraining/contracts";

export type RadarProfileStatus = "idle" | "loading" | "ready" | "empty" | "error";

export interface UseAthleteRadarProfileResult {
  status: RadarProfileStatus;
  profile: AthleteRadarProfile | null;
  error: Error | null;
}

type Action =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "ready"; profile: AthleteRadarProfile }
  | { type: "empty" }
  | { type: "error"; error: Error };

function reducer(
  state: UseAthleteRadarProfileResult,
  action: Action
): UseAthleteRadarProfileResult {
  switch (action.type) {
    case "idle":
      return state.status === "idle" ? state : initialState;
    case "loading":
      return { status: "loading", profile: null, error: null };
    case "ready":
      return { status: "ready", profile: action.profile, error: null };
    case "empty":
      return state.status === "empty" && state.profile === null
        ? state
        : { status: "empty", profile: null, error: null };
    case "error":
      return { status: "error", profile: null, error: action.error };
  }
}

const initialState: UseAthleteRadarProfileResult = {
  status: "idle",
  profile: null,
  error: null,
};

// Cheap structural compare to suppress dispatches on Firestore metadata-only
// snapshots (fromCache / hasPendingWrites flips). Mirrors the mobile slice's
// areAthleteProfilesEqual.
function areProfilesEqual(a: AthleteRadarProfile | null, b: AthleteRadarProfile | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.level !== b.level) return false;
  if (a.strongestAxis !== b.strongestAxis) return false;
  if (a.weakestAxis !== b.weakestAxis) return false;
  if (a.xp.total !== b.xp.total) return false;
  const aScoreKeys = a.scores ? (Object.keys(a.scores) as RadarAxis[]) : [];
  for (const key of aScoreKeys) {
    if (a.scores?.[key] !== b.scores?.[key]) return false;
  }
  return true;
}

/**
 * Subscribes to the authenticated athlete's radar profile document at
 * `Users/{uid}/AthleteProfile/radar` and returns the live state.
 *
 * Runtime-validates payloads via `toAthleteRadarProfile`, which also reads a
 * scores-only document (no XP awarded yet). Suppresses no-op dispatches on metadata-only snapshot echoes.
 * Cleanup runs on unmount and on uid change.
 */
export function useAthleteRadarProfile(
  uid: string | null | undefined
): UseAthleteRadarProfileResult {
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastProfileRef = useRef<AthleteRadarProfile | null>(null);

  useEffect(() => {
    if (!uid) {
      lastProfileRef.current = null;
      dispatch({ type: "idle" });
      return;
    }

    dispatch({ type: "loading" });

    const ref = doc(firebaseDB, "Users", uid, "AthleteProfile", "radar");
    const unsubscribe = onSnapshot(
      ref,
      (snapshot: DocumentSnapshot) => {
        if (!snapshot.exists()) {
          lastProfileRef.current = null;
          dispatch({ type: "empty" });
          return;
        }
        const data = toAthleteRadarProfile(snapshot.data());
        if (!data) {
          // Document exists but doesn't validate — surface as error so the UI
          // can recover rather than silently rendering corrupt scores.
          dispatch({
            type: "error",
            error: new Error("Invalid radar profile shape"),
          });
          return;
        }
        if (areProfilesEqual(lastProfileRef.current, data)) return;
        lastProfileRef.current = data;
        dispatch({ type: "ready", profile: data });
      },
      (err: FirestoreError) => {
        dispatch({ type: "error", error: err });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [uid]);

  return state;
}
